import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { TierErrorCode } from '@/utils/api/parseApiError';

// Seat-filled errors (OPS_SEATS_FILLED / RESPOND_SEATS_FILLED) have their own toast
// messaging elsewhere; this component only explains plan-upgrade-required reasons,
// so its prop type stays narrow rather than accepting the full TierErrorCode union.
export type PlanRequiredReason =
  | Extract<TierErrorCode, 'PLAN_OPS_REQUIRED' | 'PLAN_RESPOND_REQUIRED'>
  | 'ops'
  | 'respond';

interface Props {
  reason: PlanRequiredReason;
  /** Drops the title and uses body-only styling, for inline use under a disabled picker (Task 18). */
  compact?: boolean;
  /** Overrides the body text color; defaults to the muted "text.secondary" tone. */
  bodyColor?: string;
  /** Overrides the body text font size; defaults to the "body2" variant's size. */
  bodyFontSize?: string;
}

const COPY_KEYS_BY_PLAN: Record<'ops' | 'respond', { title: string; body: string }> = {
  ops: {
    title: StringKeys.PLAN_OPS_REQUIRED_TITLE,
    body: StringKeys.PLAN_OPS_REQUIRED_BODY,
  },
  respond: {
    title: StringKeys.PLAN_RESPOND_REQUIRED_TITLE,
    body: StringKeys.PLAN_RESPOND_REQUIRED_BODY,
  },
};

const toPlan = (reason: PlanRequiredReason): 'ops' | 'respond' => (
  reason === 'PLAN_OPS_REQUIRED' || reason === 'ops' ? 'ops' : 'respond'
);

const PlanExplainer: React.FC<Props> = ({
  reason,
  compact = false,
  bodyColor = 'text.secondary',
  bodyFontSize,
}) => {
  const { t } = useTranslation();
  const { title, body } = COPY_KEYS_BY_PLAN[toPlan(reason)];

  return (
    <Box>
      {!compact && <Typography variant="subtitle2">{t(title)}</Typography>}
      <Typography variant="body2" color={bodyColor} sx={bodyFontSize ? { fontSize: bodyFontSize } : undefined}>
        {t(body)}
      </Typography>
    </Box>
  );
};

export default PlanExplainer;
